// API URL - use env variable or default to /api (same origin)
const API_URL = process.env.REACT_APP_API_URL || '/api';
const DEFAULT_USER_ID = '11111111-1111-1111-1111-111111111111';

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Helper to get current user ID from token or fallback
const getCurrentUserId = (): string => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch {
      return DEFAULT_USER_ID;
    }
  }
  return DEFAULT_USER_ID;
};

export interface Lesson {
  id: string;
  name_pl: string;
  description_pl: string;
  school_grade: number;
  school_unit: number;
  difficulty_level: number;
  sort_order: number;
  word_count: number;
  sentence_count: number;
  times_completed: number;
  set_id?: string;
}

export interface LessonSet {
  id: string;
  name_pl: string;
  icon: string;
  color: string;
  sort_order: number;
  lessons: Lesson[];
}

export interface Content {
  id: string;
  english: string;
  polish: string;
  type: 'word' | 'sentence';
  category: string;
  difficulty_level: number;
  lesson_id: string;
  mastery_level?: number;
}

export interface UserStats {
  totalPoints: number;
  currentStreak: number;
  wordsLearned: number;
  lessonsCompleted: number;
  todayPoints: number;
}

// Lessons
export const getLessons = async (): Promise<Lesson[]> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/lessons?userId=${userId}`, { headers: getAuthHeaders() });
  return res.json();
};

export const getLessonSets = async (): Promise<LessonSet[]> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/lessons/sets?userId=${userId}`, { headers: getAuthHeaders() });
  return res.json();
};

export const getLesson = async (id: string): Promise<Lesson & { content: Content[] }> => {
  const res = await fetch(`${API_URL}/lessons/${id}`, { headers: getAuthHeaders() });
  return res.json();
};

export const completeLesson = async (lessonId: string): Promise<{ pointsEarned: number }> => {
  const res = await fetch(`${API_URL}/lessons/${lessonId}/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({})
  });
  return res.json();
};

// Content
export const getRandomContent = async (limit: number = 10, lessonId?: string, customSetId?: string): Promise<Content[]> => {
  let url = `${API_URL}/content/random?limit=${limit}`;
  if (lessonId) url += `&lessonId=${lessonId}`;
  if (customSetId) url += `&customSetId=${customSetId}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  return res.json();
};

// Difficult words
export const getDifficultWords = async (limit: number = 20): Promise<Content[]> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/content/difficult?userId=${userId}&limit=${limit}`, { headers: getAuthHeaders() });
  return res.json();
};

export const getDifficultWordsCount = async (): Promise<{ count: number }> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/content/difficult/count?userId=${userId}`, { headers: getAuthHeaders() });
  return res.json();
};

// Progress
export const recordProgress = async (contentId: string, correct: boolean): Promise<any> => {
  const res = await fetch(`${API_URL}/progress/record`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contentId, correct })
  });
  return res.json();
};

// Review
export const getReviewItems = async (limit: number = 10): Promise<Content[]> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/review/${userId}?limit=${limit}`, { headers: getAuthHeaders() });
  return res.json();
};

export const getReviewCount = async (): Promise<{ dueCount: number }> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/review/${userId}/count`, { headers: getAuthHeaders() });
  return res.json();
};

export const completeReview = async (): Promise<{ pointsEarned: number }> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/review/${userId}/complete`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  return res.json();
};

// Scores
export const getUserStats = async (): Promise<UserStats> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/scores/${userId}/summary`, { headers: getAuthHeaders() });
  return res.json();
};

export const awardBonus = async (points: number, actionType: string): Promise<any> => {
  const res = await fetch(`${API_URL}/scores/bonus`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ points, actionType })
  });
  return res.json();
};

// User
export const updateStreak = async (): Promise<{ streak: number; bonus: number }> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/users/${userId}/streak`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  return res.json();
};

// Dictionary
export interface DictionaryItem {
  id: string;
  english: string;
  polish: string;
  type: 'word' | 'sentence';
  lesson_id: string;
  lesson_name: string;
  set_name: string;
  set_icon: string;
  times_studied: number;
  times_correct: number;
  mastery_level: number;
  memorization_level: number;
  last_studied: string;
}

export interface DictionaryStats {
  words_count: number;
  sentences_count: number;
  total_count: number;
  avg_mastery: number;
}

export const getDictionary = async (groupBy: 'alphabetical' | 'lesson' = 'alphabetical', sortLang: 'english' | 'polish' = 'english'): Promise<DictionaryItem[]> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/dictionary?userId=${userId}&groupBy=${groupBy}&sortLang=${sortLang}`, { headers: getAuthHeaders() });
  return res.json();
};

export const getDictionaryStats = async (): Promise<DictionaryStats> => {
  const userId = getCurrentUserId();
  const res = await fetch(`${API_URL}/dictionary/stats?userId=${userId}`, { headers: getAuthHeaders() });
  return res.json();
};

// Custom Sets
export interface CustomSet {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  content_count?: number;
  lesson_count?: number;
  total_words?: number;
}

export interface CustomSetWithContent extends CustomSet {
  content: Content[];
  lessons: Lesson[];
  word_count: number;
  sentence_count: number;
}

export interface CustomSetItems {
  content: Array<{
    item_id: string;
    item_type: 'content';
    id: string;
    english: string;
    polish: string;
    type: 'word' | 'sentence';
    lesson_name: string;
  }>;
  lessons: Array<{
    item_id: string;
    item_type: 'lesson';
    id: string;
    name_pl: string;
    description_pl: string;
    content_count: number;
  }>;
}

export const getCustomSets = async (): Promise<CustomSet[]> => {
  const res = await fetch(`${API_URL}/custom-sets`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch custom sets');
  return res.json();
};

export const getCustomSet = async (id: string): Promise<CustomSetWithContent> => {
  const res = await fetch(`${API_URL}/custom-sets/${id}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch custom set');
  return res.json();
};

export const createCustomSet = async (data: { name: string; description?: string; icon?: string; color?: string }): Promise<CustomSet> => {
  const res = await fetch(`${API_URL}/custom-sets`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to create custom set');
  return res.json();
};

export const updateCustomSet = async (id: string, data: Partial<CustomSet>): Promise<CustomSet> => {
  const res = await fetch(`${API_URL}/custom-sets/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error('Failed to update custom set');
  return res.json();
};

export const deleteCustomSet = async (id: string): Promise<void> => {
  const res = await fetch(`${API_URL}/custom-sets/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Failed to delete custom set');
};

export const addContentToCustomSet = async (setId: string, contentId: string): Promise<void> => {
  const res = await fetch(`${API_URL}/custom-sets/${setId}/content`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contentId })
  });
  if (!res.ok) throw new Error('Failed to add content to set');
};

export const addBulkContentToCustomSet = async (setId: string, contentIds: string[]): Promise<void> => {
  const res = await fetch(`${API_URL}/custom-sets/${setId}/content/bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ contentIds })
  });
  if (!res.ok) throw new Error('Failed to add content to set');
};

export const addLessonToCustomSet = async (setId: string, lessonId: string): Promise<void> => {
  const res = await fetch(`${API_URL}/custom-sets/${setId}/lesson`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ lessonId })
  });
  if (!res.ok) throw new Error('Failed to add lesson to set');
};

export const removeContentFromCustomSet = async (setId: string, contentId: string): Promise<void> => {
  const res = await fetch(`${API_URL}/custom-sets/${setId}/content/${contentId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Failed to remove content from set');
};

export const removeLessonFromCustomSet = async (setId: string, lessonId: string): Promise<void> => {
  const res = await fetch(`${API_URL}/custom-sets/${setId}/lesson/${lessonId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error('Failed to remove lesson from set');
};

export const getCustomSetItems = async (setId: string): Promise<CustomSetItems> => {
  const res = await fetch(`${API_URL}/custom-sets/${setId}/items`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch custom set items');
  return res.json();
};

// Word lookup for sentence breakdown
export const lookupWords = async (words: string[]): Promise<Record<string, string>> => {
  const res = await fetch(`${API_URL}/content/lookup?words=${encodeURIComponent(words.join(','))}`, { headers: getAuthHeaders() });
  return res.json();
};
